import type { CSSProperties } from 'react';
import { useMemo } from 'react';
import { Position } from '@xyflow/react';
import { useWorkflowStore, type LayoutOrientation } from '@/stores/workflowStore';
import { FLOATING_ICON_BOX_SIZE } from '@/utils/workflow';

type HandlePositions = {
  input: Position;
  output: Position;
};

/**
 * Returns handle positions based on layout orientation
 * - Horizontal (LR): input on Left, output on Right
 * - Vertical (TB): input on Top, output on Bottom
 */
export const getHandlePositions = (orientation: LayoutOrientation): HandlePositions => {
  if (orientation === 'horizontal') {
    return {
      input: Position.Left,
      output: Position.Right,
    };
  }
  return {
    input: Position.Top,
    output: Position.Bottom,
  };
};

/**
 * Hook that returns handle positions based on current layout orientation
 */
export const useHandlePositions = (): HandlePositions => {
  const orientation = useWorkflowStore((state) => state.layoutOrientation);
  return getHandlePositions(orientation);
};

type HandleSlot = { position: Position; style: CSSProperties };
type HandleLayout = { input: HandleSlot; output: HandleSlot };

const DEFAULT_ICON_BOX_SIZE = FLOATING_ICON_BOX_SIZE;

/**
 * Returns both the React Flow `Position` and the inline `style` each handle
 * needs so it sits on the edge of the **icon box** (not the wrapper, which
 * also contains label rows below the icon).
 *
 * Horizontal mode: handles sit on the left/right edges of the icon, vertically
 * centered on the icon box (`top: iconBoxSize / 2`).
 *
 * Vertical mode: handles sit on the top/bottom edges of the icon (`top: 0` /
 * `top: iconBoxSize`), horizontally centered on the icon box. Using an
 * explicit `top: iconBoxSize` for the Bottom handle — instead of letting it
 * default to the wrapper's `100%` — keeps edges connecting at the icon's
 * bottom rather than below the label text.
 *
 * @param iconBoxSize Defaults to the standard 56px floating-node icon box.
 *                    Pass a different value for smaller node variants
 *                    (e.g. PlaceholderTriggerNode at 40px).
 */
export const getHandleLayout = (
  orientation: LayoutOrientation,
  iconBoxSize: number = DEFAULT_ICON_BOX_SIZE,
): HandleLayout => {
  const center = iconBoxSize / 2;
  if (orientation === 'horizontal') {
    return {
      input: { position: Position.Left, style: { top: `${center}px` } },
      output: { position: Position.Right, style: { top: `${center}px` } },
    };
  }
  return {
    input: { position: Position.Top, style: { top: 0, left: `${center}px` } },
    output: { position: Position.Bottom, style: { top: `${iconBoxSize}px`, left: `${center}px` } },
  };
};

/**
 * Hook variant of {@link getHandleLayout}. Subscribes only to
 * `layoutOrientation`, so consumers re-render only when the orientation flips.
 *
 * The returned object is memoized on `(orientation, iconBoxSize)` so unrelated
 * parent re-renders (selection, hover, execution-status changes — all frequent
 * on the workflow canvas) don't produce a fresh layout object and force the
 * downstream `<Handle style={...}>` to re-apply identical styles.
 */
export const useHandleLayout = (iconBoxSize?: number): HandleLayout => {
  const orientation = useWorkflowStore((state) => state.layoutOrientation);
  return useMemo(() => getHandleLayout(orientation, iconBoxSize), [orientation, iconBoxSize]);
};
